import type { TaskStatus } from '@/domains/task/enums';
import type { ImageToTextResult } from '@/infrastructure/api-functions/core-drafting-api/create-novelty-check-task-api';
import type { NoveltyCheckTaskStep } from '../enums';
import type { RETRIEVAL_STRATEGY } from '../constants';
import type { RetrievalElement } from '../apis/retrieval-elements-api';
import type { PatentEvidenceData } from '../apis/novelty-search-pr-api';

export interface TechnicalFeaturesDto {
    feature_elements: string
    feature_function: string
    feature_num: string
    /**
     * 拆解的特征(原始文本)
     */
    feature_text_original: string
    /**
     * 特征类型 topic (主题技术特征) 打勾 旗子
     * core (区别技术特征) 打勾 旗子
     * public (现有技术特征) 打勾
     */
    feature_weight: 'topic' | 'core' | 'public' | ''
    /**
     * 特征权重原因
     */
    feature_weight_reason: string
    /**
     * 是否用户添加
     */
    user_add: boolean
    /**
     * 技术特征组成部分
     */
    components?: string[]
}

/**
 * 技术特征
 * @description 从用户输入的技术要点中提取的技术特征
 */

export interface TechFeature {
    /**
     * 技术特征名称
     */
    name: string;
    /**
     * 技术特征原因
     */
    reason: string;
    // /**
    //  * 技术特征关键词
    //  */
    // keywords: string[];
    // /**
    //  * 技术特征得分
    //  */
    // score: number;
    /**
     * 是否选中
     */
    selected: boolean;
    /**
     * 是否被标记
     * @description 是否被标记的技术特征，默认值为 false
     */
    flagged: boolean;
    /**
     * 是否人工添加
     * @description 是否人工添加的技术特征，默认值为 false
     */
    manualAdded: boolean;
    /**
     * 特征权重
     */
    featureWeight: 'topic' | 'core' | 'public' | '';
    /**
     * 原始数据
     */
    originData: TechnicalFeaturesDto
    /**
     * 技术特征组成部分
     */
    components: string[];
}
/**
 * 特征类型
 * @description 特征类型：public 现有技术、obvious 显而易见、not_public 非现有技术
 */
export type TechFeatureType = 'public' | 'obvious' | 'not_public';
/**
 * 包含匹配特征的专利信息
 */
export interface PatentProfileWithMatchedTechFeature {
    /**
     * 对比文献类型
     */
    comparativeLiteratureType: 'X' | 'Y' | 'A';
    /**
     * 专利 id
     */
    patentId: string;
    /**
     * 匹配特征
     */
    matchedTechFeatures: MatchedTechFeature[];
    /**
     * 是否选中
     */
    selected: boolean;
    /**
     * 是否手动添加
     */
    userAdded: boolean;
    /**
     * 是否最相似
     */
    mostSimilar: boolean;
    /**
     * 提取专利特征所用到的语言
     */
    lang: string;
    /**
     * 匹配度得分
     */
    publicScore: number
    /**
     * 是否已经抽取了特征
     */
    extracted: boolean;
}

/**
 * 包含匹配特征的文档信息（用于论文和网站）
 * @description 与专利信息类似，但使用 docId 和 docTitle 替代 patentId
 */
export interface DocProfileWithMatchedTechFeature {
    /**
     * 对比文献类型
     */
    comparativeLiteratureType: 'X' | 'Y' | 'A';
    /**
     * 文档 id
     */
    docId: string;
    /**
     * 文档标题
     */
    docTitle: string;
    /**
     * 文档 URL（可选，用于论文和网站）
     */
    docUrl?: string;
    /**
     * 匹配特征
     */
    matchedTechFeatures: MatchedTechFeature[];
    /**
     * 是否选中
     */
    selected: boolean;
    /**
     * 是否手动添加
     */
    userAdded: boolean;
    /**
     * 是否最相似
     */
    mostSimilar: boolean;
    /**
     * 提取文档特征所用到的语言
     */
    lang: string;
    /**
     * 匹配度得分
     */
    publicScore: number
    /**
     * 是否已经抽取了特征
     */
    extracted: boolean;
    /**
     * 发表时间
     * @description 格式为 YYYYMMDD 的数字，如 20201212
     */
    pbd?: number;
}

/**
 * 包含匹配特征的论文信息
 * @description 继承文档基础类型，新增论文专用字段
 */
export interface PaperProfileWithMatchedTechFeature extends DocProfileWithMatchedTechFeature {
    /**
     * 期刊名称
     */
    journal?: string;
    /**
     * 作者
     */
    author?: string;
    /**
     * 摘要
     */
    abstract?: string;
}

/**
 * 包含匹配特征的网站信息
 * @description 继承文档基础类型，新增网站专用字段
 */
export interface WebsiteProfileWithMatchedTechFeature extends DocProfileWithMatchedTechFeature {
    /**
     * 来源网站
     */
    source?: string;
}

/**
 * 技术特征同义词
 */
export interface TechFeatureSynonym {
    /**
     * 技术特征
     */
    techFeature: string;
    /**
     * 技术特征翻译
     * @description 用途未知
     */
    translation: string;
    /**
     * 同义词
     */
    synonym: string[];
    /**
     * 同义词得分
     */
    synonymScore: number[];
    /**
     * @description 用途未知
     */
    words: Omit<TechFeatureSynonym, 'words'>[];
}

/**
 * 参数名称
 */
export enum Parameter {
    FEATURES = 'features',
    IPC = 'ipc',
    CPC = 'cpc',
}

/**
 * 检索结果
 */
export interface RetrievalResult {
    /**
     * 检索思路
     */
    idea: string;
    /**
     * 检索表达式
     */
    searchExpress: string;
    /**
     * 检索结果
     */
    patents: {
        patentId: string;
        score?: number;
    }[];
    /**
     * 检索策略参数
     */
    strategyParameters: {
        name: Parameter;
        value: string[];
    }[];
    /**
     * 检索是否完成
     * @description 检索+技术特征识别完成
     */
    finished: boolean;
    /**
     * 下一步计划
     */
    nextPlan: string;
}

/**
 * 检索步骤
 */
export interface RetrievalStep {
    /**
     * 检索步骤 id
     */
    id: string;
    /**
     * 检索步骤名称
     */
    name: RETRIEVAL_STRATEGY;
    /**
     * 检索步骤是否运行中
     */
    running: boolean;
}

/**
 * 检索进度状态
 */
export interface RetrievalProgressState {
    /**
     * 检索耗时
     */
    timeSpend: number;
    /**
     * 检索节约时间
     */
    timeSaved: number;
    /**
     */
    steps: RetrievalStep[];
    /**
     * 检索结果
     */
    result: Map<RETRIEVAL_STRATEGY, RetrievalResult>;
}

/**
 * @description 当前匹配专利排行榜状态
 */
export interface CurrentMatchedPatentState {
    /**
     * 当前匹配专利排行榜
     */
    ranking: PatentProfileWithMatchedTechFeature[];
    /**
     * 手动添加的匹配专利
     */
    manualAdded: PatentProfileWithMatchedTechFeature[];
    /**
     * 额外搜索的新专利
     */
    extraAdded: PatentProfileWithMatchedTechFeature[];
}

/**
 * @description 当前匹配文档排行榜状态（通用于论文和网站）
 * @deprecated 使用 CurrentMatchedPaperState 或 CurrentMatchedWebsiteState 替代
 */
export interface CurrentMatchedDocState {
    /**
     * 当前匹配文档排行榜
     */
    ranking: DocProfileWithMatchedTechFeature[];
    /**
     * 手动添加的匹配文档
     */
    manualAdded: DocProfileWithMatchedTechFeature[];
    /**
     * 额外搜索的新文档
     */
    extraAdded: DocProfileWithMatchedTechFeature[];
}

/**
 * @description 当前匹配论文排行榜状态
 */
export interface CurrentMatchedPaperState {
    /**
     * 当前匹配论文排行榜
     */
    ranking: PaperProfileWithMatchedTechFeature[];
    /**
     * 手动添加的匹配论文
     */
    manualAdded: PaperProfileWithMatchedTechFeature[];
    /**
     * 额外搜索的新论文
     */
    extraAdded: PaperProfileWithMatchedTechFeature[];
}

/**
 * @description 当前匹配网站排行榜状态
 */
export interface CurrentMatchedWebsiteState {
    /**
     * 当前匹配网站排行榜
     */
    ranking: WebsiteProfileWithMatchedTechFeature[];
    /**
     * 手动添加的匹配网站
     */
    manualAdded: WebsiteProfileWithMatchedTechFeature[];
    /**
     * 额外搜索的新网站
     */
    extraAdded: WebsiteProfileWithMatchedTechFeature[];
}

/**
 * 检索类型
 * @description 用于区分专利检索和非专利文献检索
 */
export type RetrievalType = 'patent' | 'non-patent';

/**
 * @description 当前选中的检索类型状态
 */
export interface CurrentSelectedRetrievalTypeState {
    /**
     * 当前选中的检索类型
     * @description 'patent' 表示专利检索，'non-patent' 表示非专利文献检索（包括论文和网站）
     */
    selectedType: RetrievalType;
}

/**
 * 检索要素及同义词
 */
export interface RetrievalElementsAndSynonyms {
    /**
     * 检索要素
     */
    element: string;
    /**
     * 同义词
     */
    synonyms: string[];
}

/**
 * 检索策略
 */
export interface RetrievalStrategy {
    /**
     * 检索策略名称
     * @description 检索方法：语义检索、分类号检索、渐进式检索
     */
    name: string;
    /**
     * 检索策略内容
     * @description 检索式
     */
    content: string;
}


/**
 * 评述项类型
 */
export type NoveltyReviewType = 'Y' | 'X' | 'A';

/**
 * 评述分类
 */
export type NoveltyReviewCategory = 'creativity' | 'novelty';

/**
 * 单个评述项
 */
export interface NoveltyReviewItem {
    /**
     * 评述类型
     */
    type: NoveltyReviewType;
    /**
     * 评述分类
     */
    category: NoveltyReviewCategory;
    /**
     * 评述标题
     */
    title: string;
    /**
     * 评述内容
     */
    content: string;
}
/**
 * 检索结果报告状态
 */
export interface RetrievalResultReportState {
    /**
     * 检索结果报告标题
     */
    title: string;
    /**
     * 用户原始输入
     * @description 创建任务时的原始输入文本
     */
    userInput: string;
    /**
     * 翻译后的用户输入
     */
    translationInput?: string;
    /**
     * 用户输入的（或 AI 总结用户输入）技术要点
     */
    techEssential: string;
    /**
     * 公开日期开始
     */
    pbdStart?: string;
    /**
     * 公开日期结束
     */
    pbdEnd?: string;
    /**
     * 检索技术特征及关键词
     * @description 技术特征
     */
    techFeatures: TechFeature[];
    /**
     * 技术特征阈值
     */
    threshold: number;
    /**
     * 技术特征与对比文献
     */
    techFeaturesWithComparedPats: {
        patentId: string;
        publicScore: number;
        relatedPara: string;
        pn: string;
    }[];
    /**
     * 新颖性评述
     */
    noveltyReview: NoveltyReviewItem[];
    /**
     * 创造性评述
     */
    creativityReview: string[];
    /**
     * 创建者
     */
    creator: string;
    /**
     * 生成时间
     */
    generatedTime: number;
    /**
     * 搜索数量
     */
    searchQty: number;
    /**
     * 对比数量
     */
    comparativeQty: number;
    /**
     * 最接近的对比文献数量
     */
    nearestQty: number;
    /**
     * 次接近的对比文献数量
     */
    secondaryApproachQty: number;
    /**
     * 检索要素原始数据列表
     * @description 用于组件显示的原始检索要素数据
     */
    elements: RetrievalElement[];
    /**
     * 检索要素是否合并
     */
    familyMergeEnabled: boolean;

    /**
     * 检索要素加载状态
     */
    elementsLoading: boolean;
    /**
     * 图片转文本结果
     * @description 当用户上传图片进行识别时，此字段包含识别结果
     */
    imageToTextResult?: ImageToTextResult;
    /**
     * 用户选中的图片（stored_url 列表）
     * @description 用于过滤参与附图识别的图片，为空时使用所有图片
     */
    selectedImages?: string[];
    /**
     * 专利常见证据数据
     * @description 存储每个专利的常见证据检索任务数据，包括job_id和判断结果
     * - key: patent_id (专利ID)
     * - value: PatentEvidenceData (包含 job_id 和可选的 judge_result)
     *
     * 业务流程:
     * 1. 调用 ccCommonEvidence 接口创建任务,获取初始数据 (只有 job_id)
     * 2. 使用 task_id + patent_id 轮询 pollCommonEvidenceResult 接口
     * 3. 轮询成功后,judge_result 字段会被填充
     */
    patentCommonEvidenceData: Record<string, PatentEvidenceData>;
}

/**
 * 专利匹配到的技术特征
 */
export interface MatchedTechFeature {
    /**
     * 技术特征文本 {@link TechFeature.name}
     */
    techFeature: string;
    /**
     * 对比技术特征文本
     * @description 专利中的技术特征的文本
     */
    comparisonFeature: string;
    /**
     * 相似度得分？？？
     */
    score: number;
    /**
     * 特征匹配得分？？？
     */
    publicScore: number;
    /**
     * 是否相似
     */
    similar: boolean;
    /**
     * 特征类型
     * @description 特征类型：public 现有技术、obvious 显而易见、not_public 非现有技术
     */
    type: TechFeatureType;
    /**
     * 模型输出的原始特征类型文本
     * @description 模型输出的原始文本，用于保留模型的原始判断
     */
    originalType: string;
    /**
     * 特征类型原因
     * @description 说明该特征被标记为某种类型的原因
     */
    reason: string;
    /**
     * 模型输出的原始特征类型原因
     * @description 模型输出的原始原因文本，用于保留模型的原始判断
     */
    originalReason: string;
}

/**
 * 特征提取结果
 */
export interface PatentFeatureExtractorResult {
    /**
     * 专利特征
     */
    features: MatchedTechFeature[];
    /**
     * 特征匹配得分？？？
     */
    publicScore: number;
}

/**
 * 技术特征与对比文献结果
 */
export interface TechFeatureWithComparedPats {
    /**
     * 专利号
     */
    patentNumber: string;
    /**
     * 本发明技术要点
     */
    techAbstract: string;
    /**
     * 本发明公开度
     * @description 0-100
     */
    score: number;
    /**
     * 对比文献公开情况？？？
     */
    result: unknown[];
}

/**
 * 专利新颖性检查任务状态
 */
export interface NoveltyCheckTaskStatus {
    /**
     * 任务状态
     */
    taskStatus: TaskStatus;
    /**
     * 任务步骤
     */
    taskStep: NoveltyCheckTaskStep;
    /**
     * 最后更新时间
     * @description 按用户设置时区格式化
     */
    lastUpdateTime: string;
    /**
     * 查新输入
     */
    userInput: string;
    /**
     * 输入语言（大写字母）
     * @description 用户查新原始输入（或经总结）文本的语言,
     * @example 'CN' | 'EN'
     */
    inputLanguage: string;
    /**
     * 翻译后的用户输入
     */
    translationInput?: string;
    /**
     * 图片转文本结果
     * @description 当用户上传图片进行识别时，此字段包含识别结果
     */
    imageToTextResult?: ImageToTextResult;
}


/**
 * 表示关键词或IPC项的数据结构
 */
export interface ItemWithSourceDto {
    /**
     * 文本内容（关键词或IPC分类号）
     */
    text: string;
    /**
     * 数据来源标识
     */
    source: boolean;
}

/**
 * 表示检索要素中的表达式数据结构
 */
export interface RetrievalElementExpressDto {
    /**
     * 关键词
     */
    text: string;
    /**
     * 数据来源
     */
    source: boolean;
    /**
     * 拓展词列表
     */
    keyword: ItemWithSourceDto[];
    /**
     * IPC分类号列表
     */
    ipc: ItemWithSourceDto[];
}

/**
 * 表示检索要素数据结构
 */
export interface RetrievalElementDto {
    /**
     * 已勾选的特征
     */
    feature_text_original: string;
    /**
     * 特征类型
     * topic_search - 主题要素
     * key_search - 关键要素
     * sup_search - 补充要素
     */
    feature_type: 'topic_search' | 'key_search' | 'sup_search';
    /**
     * 表达式列表
     */
    express: RetrievalElementExpressDto[];
}

/**
 * 重置任务请求参数
 * @description 用于重置查新任务的请求数据
 */
export interface ResetTaskRequest {
    /**
     * 任务ID
     * @description 需要重置的查新任务唯一标识符
     */
    task_id: string;
}

/**
 * 重置任务响应数据
 * @description 重置任务操作的响应结果
 */
export interface ResetTaskResponse {
    /**
     * 操作是否成功
     */
    success?: boolean;
    /**
     * 响应消息
     */
    message?: string;
}

/**
 * 查新步骤信息状态枚举
 */
export enum NoveltyCheckStepInfoStatus {
    /**
     * 等待中
     */
    PENDING = 'pending',
    /**
     * 进入中
     */
    ENTERING = 'entering',
    /**
     * 空闲中
     */
    IDLE = 'idle',
    /**
     * 离开中
     */
    LEAVING = 'leaving',
    /**
     * 已离开
     */
    LEFT = 'left',
    /**
     * 错误状态
     */
    ERROR = 'error',
}

/**
 * 查新步骤信息
 * @description 存储查新任务进行步骤中产生的状态数据
 */
export interface NoveltyCheckStepInfo {
    /**
     * 当前状态
     */
    status: NoveltyCheckStepInfoStatus;
    /**
     * 状态时间流
     * @description 记录每个状态进入的时间戳，状态切换后才有切换到的这个状态的key
     */
    waterflow: Partial<Record<NoveltyCheckStepInfoStatus, number>>;
}
